import {
  useEffect,
  useRef,
  useState,
  type CSSProperties,
  type MutableRefObject,
  type RefObject,
} from 'react';
import styled, {css, keyframes} from 'styled-components';

/**
 * The storm clouds of Poisson rain: a far overcast deck on one canvas, and
 * a near bank of cumulus with one canvas each, so every cloud drifts and
 * bobs at its own pace. Each is painted once and then only moved, so the
 * sky costs nothing per frame. Every shower paints a new sky from a fresh
 * seed: clouds of different sizes at uneven spacing, lit as one mass with
 * bright tops and dark, flat undersides. The sky also tells the rain where
 * each cloud's base is, so drops leave the cloud above them.
 */

/** How far the far deck drifts each way, in px; its canvas overhangs by this. */
const DRIFT_PX = 70;

/** The sky's height at full size, in px. */
const SKY_HEIGHT = 240;

/** Where rain starts where no cloud hangs, in px from the top at full size. */
const BARE_SKY_BASE = 70;

/** How one layer of cloud is lit, from its top down to its base. */
interface CloudLight {
  top: string;
  upper: string;
  lower: string;
  base: string;
  /** How strong the soft patches of sunlight on the upper puffs are. */
  highlight: number;
  /** The colour of the shadows in the creases, as "r, g, b". */
  shadow: string;
}

const NEAR_LIGHT: CloudLight = {
  top: '#fbfcfd',
  upper: '#e5eaf1',
  lower: '#a9b4c4',
  base: '#6f7b91',
  highlight: 0.6,
  shadow: '66, 78, 100',
};

const FAR_LIGHT: CloudLight = {
  top: '#c7cfda',
  upper: '#aab4c3',
  lower: '#8a95a8',
  base: '#727e95',
  highlight: 0.25,
  shadow: '58, 68, 88',
};

/** Mulberry32: a small seeded random source in [0, 1). */
function seededRandom(seed: number): () => number {
  let state = seed;
  return () => {
    state = (state + 0x6d2b79f5) | 0;
    let t = Math.imul(state ^ (state >>> 15), 1 | state);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

/** How big the sky is drawn for a screen this wide. */
function skyScale(screenWidth: number): number {
  if (screenWidth <= 640) {
    return 0.7;
  }
  return screenWidth <= 1100 ? 0.85 : 1;
}

/** One soft puff of cloud. */
interface Puff {
  x: number;
  y: number;
  r: number;
  /** How high in its cloud it sits: 0 at the base, 1 at the top. */
  height: number;
}

/**
 * Paints puffs as one soft white silhouette. Each puff fades out at its
 * edge, so overlaps merge without seams and the outline stays fluffy.
 */
function paintSilhouette({
  context,
  puffs,
  squash = 1,
}: {
  context: CanvasRenderingContext2D;
  puffs: readonly Puff[];
  squash?: number;
}) {
  for (const puff of puffs) {
    context.save();
    context.translate(puff.x, puff.y);
    context.scale(1, squash);
    const fill = context.createRadialGradient(0, 0, 0, 0, 0, puff.r);
    fill.addColorStop(0, 'rgba(255, 255, 255, 1)');
    fill.addColorStop(0.6, 'rgba(255, 255, 255, 0.95)');
    fill.addColorStop(1, 'rgba(255, 255, 255, 0)');
    context.fillStyle = fill;
    context.beginPath();
    context.arc(0, 0, puff.r, 0, Math.PI * 2);
    context.fill();
    context.restore();
  }
}

/** A soft round patch of colour, fading out to its edge. */
function patch({
  context,
  x,
  y,
  r,
  color,
}: {
  context: CanvasRenderingContext2D;
  x: number;
  y: number;
  r: number;
  color: string;
}) {
  const fill = context.createRadialGradient(x, y, 0, x, y, r);
  fill.addColorStop(0, `rgba(${color})`);
  fill.addColorStop(1, `rgba(${color.replace(/[\d.]+$/, '0')})`);
  context.fillStyle = fill;
  context.fillRect(x - r, y - r, 2 * r, 2 * r);
}

/**
 * Lights a silhouette already on the canvas as one mass: bright on top,
 * dark and flat at the base, with soft sunlit patches on the higher puffs
 * and shadow in the creases low down. Paints only where the cloud is.
 */
function shadeSilhouette({
  context,
  puffs,
  top,
  base,
  left,
  right,
  light,
  random,
}: {
  context: CanvasRenderingContext2D;
  puffs: readonly Puff[];
  top: number;
  base: number;
  left: number;
  right: number;
  light: CloudLight;
  random: () => number;
}) {
  context.globalCompositeOperation = 'source-atop';
  const body = context.createLinearGradient(0, top, 0, base);
  body.addColorStop(0, light.top);
  body.addColorStop(0.4, light.upper);
  body.addColorStop(0.78, light.lower);
  body.addColorStop(1, light.base);
  context.fillStyle = body;
  context.fillRect(left, top, right - left, base - top + 200);
  for (const puff of puffs) {
    if (puff.height > 0.35 && random() < 0.6) {
      patch({
        context,
        x: puff.x - puff.r * 0.35,
        y: puff.y - puff.r * 0.45,
        r: puff.r * 1.1,
        color: `255, 255, 255, ${light.highlight}`,
      });
    }
  }
  for (const puff of puffs) {
    if (puff.height < 0.6 && random() < 0.45) {
      patch({
        context,
        x: puff.x + puff.r * 0.3,
        y: puff.y + puff.r * 0.4,
        r: puff.r * 1.05,
        color: `${light.shadow}, 0.28`,
      });
    }
  }
  context.globalCompositeOperation = 'source-over';
}

/**
 * One cumulus: a dome of puffs over a flat shelf, with smaller puffs along
 * its top for the cauliflower look.
 */
function cumulusPuffs({
  random,
  cx,
  baseY,
  width,
  height,
}: {
  random: () => number;
  cx: number;
  baseY: number;
  width: number;
  height: number;
}): Puff[] {
  const puffs: Puff[] = [];
  const dome = (u: number) => Math.sqrt(Math.max(0, 1 - (2 * u - 1) ** 2));
  const bodyCount = Math.round(16 + width / 16);
  for (let i = 0; i < bodyCount; i += 1) {
    const u = random();
    const rise = dome(u);
    const r = height * (0.16 + 0.2 * random()) * (0.6 + 0.55 * rise);
    const crest = baseY - height * rise * (0.7 + 0.3 * random());
    const lowest = baseY - r * 0.55;
    const highest = crest + r;
    const y =
      highest >= lowest ? lowest : highest + random() * (lowest - highest);
    puffs.push({x: cx + (u - 0.5) * width * 0.94, y, r, height: 0});
  }
  for (let i = 0; i < Math.round(bodyCount * 0.25); i += 1) {
    const u = 0.15 + 0.7 * random();
    const r = height * (0.1 + 0.08 * random());
    // Sunk well into the dome, so they bulge its outline, not float above.
    const y = baseY - height * dome(u) * (0.8 + 0.12 * random()) + r * 1.3;
    puffs.push({x: cx + (u - 0.5) * width * 0.9, y, r, height: 0});
  }
  const shelf = Math.max(3, Math.round(width / 34));
  for (let i = 0; i < shelf; i += 1) {
    const u = (i + 0.5 + (random() - 0.5) * 0.6) / shelf;
    const r = height * (0.13 + 0.07 * random());
    puffs.push({
      x: cx + (u - 0.5) * width * 0.78,
      y: baseY - r * 0.6,
      r,
      height: 0,
    });
  }
  for (const puff of puffs) {
    puff.height = (baseY - puff.y) / height;
  }
  return puffs;
}

/** One near cloud, ready to paint on its own canvas. */
interface Cumulus {
  puffs: Puff[];
  /** The flat underside, in sky px. */
  baseY: number;
  /** The span its base covers, in sky px. */
  spanLeft: number;
  spanRight: number;
  /** Its canvas's box, in sky px. */
  left: number;
  top: number;
  width: number;
  height: number;
  /** How it moves: a slow drift and a slower bob, each with its own phase. */
  driftPx: number;
  driftSec: number;
  bobSec: number;
  phaseSec: number;
  /** How long after the others it rolls in, in ms. */
  arriveMs: number;
}

/** A painted sky's layout. */
interface SkyPlan {
  scale: number;
  deckWidth: number;
  deckHeight: number;
  clouds: Cumulus[];
}

/**
 * Lays out the near bank: clouds overlapping enough that no gap opens as
 * they drift, with bases at different heights.
 */
function planSky({
  screenWidth,
  random,
}: {
  screenWidth: number;
  random: () => number;
}): SkyPlan {
  const scale = skyScale(screenWidth);
  const clouds: Cumulus[] = [];
  let x = (-120 + random() * 60) * scale;
  while (x < screenWidth + 80 * scale) {
    const cloudWidth = (230 + random() * 320) * scale;
    const baseY = (104 + random() * 56) * scale;
    const cloudHeight = Math.min(
      cloudWidth * (0.32 + random() * 0.22),
      baseY + 10 * scale,
    );
    const cx = x + cloudWidth / 2;
    const puffs = cumulusPuffs({
      random,
      cx,
      baseY,
      width: cloudWidth,
      height: cloudHeight,
    });
    const pad = 3;
    const left = Math.min(...puffs.map(puff => puff.x - puff.r)) - pad;
    const right = Math.max(...puffs.map(puff => puff.x + puff.r)) + pad;
    const top = Math.min(...puffs.map(puff => puff.y - puff.r)) - pad;
    const bottom = Math.max(...puffs.map(puff => puff.y + puff.r)) + pad;
    clouds.push({
      puffs,
      baseY,
      spanLeft: cx - cloudWidth * 0.4,
      spanRight: cx + cloudWidth * 0.4,
      left,
      top,
      width: right - left,
      height: bottom - top,
      // Neighbours overlap by at least 87 px at full size, so two swaying
      // apart by up to 68 px never open a gap.
      driftPx: (18 + random() * 16) * scale,
      driftSec: 12 + random() * 12,
      bobSec: 7 + random() * 6,
      phaseSec: random() * 30,
      arriveMs: random() * 450,
    });
    // Step by less than a cloud, so neighbours overlap by a third or more.
    x += cloudWidth * (0.42 + random() * 0.2);
  }
  return {
    scale,
    deckWidth: screenWidth + 2 * DRIFT_PX,
    deckHeight: Math.ceil(SKY_HEIGHT * scale),
    clouds,
  };
}

/** Paints the far overcast deck, which covers the top edge of the sky. */
function paintDeck({
  context,
  width,
  scale,
  random,
}: {
  context: CanvasRenderingContext2D;
  width: number;
  scale: number;
  random: () => number;
}) {
  const band = context.createLinearGradient(0, 0, 0, 100 * scale);
  band.addColorStop(0, 'rgba(255, 255, 255, 0.97)');
  band.addColorStop(0.55, 'rgba(255, 255, 255, 0.85)');
  band.addColorStop(1, 'rgba(255, 255, 255, 0)');
  context.fillStyle = band;
  context.fillRect(0, 0, width, 100 * scale);
  const puffs: Puff[] = [];
  let x = -40 * scale;
  while (x < width + 60 * scale) {
    const r = (48 + random() * 56) * scale;
    const y = (20 + random() * 44) * scale;
    puffs.push({x, y, r, height: 1 - y / (90 * scale)});
    x += (45 + random() * 80) * scale;
  }
  paintSilhouette({context, puffs, squash: 0.62});
  shadeSilhouette({
    context,
    puffs,
    top: 0,
    base: 110 * scale,
    left: 0,
    right: width,
    light: FAR_LIGHT,
    random,
  });
}

/** Paints one cumulus onto its own canvas, lit as one mass. */
function paintCumulus({
  canvas,
  cloud,
  ratio,
  random,
}: {
  canvas: HTMLCanvasElement;
  cloud: Cumulus;
  ratio: number;
  random: () => number;
}) {
  const context = canvas.getContext('2d');
  if (!context) {
    return;
  }
  canvas.width = Math.ceil(cloud.width * ratio);
  canvas.height = Math.ceil(cloud.height * ratio);
  context.setTransform(
    ratio,
    0,
    0,
    ratio,
    -cloud.left * ratio,
    -cloud.top * ratio,
  );
  context.clearRect(cloud.left, cloud.top, cloud.width, cloud.height);
  paintSilhouette({context, puffs: cloud.puffs});
  shadeSilhouette({
    context,
    puffs: cloud.puffs,
    top: cloud.top,
    base: cloud.baseY + 6,
    left: cloud.left,
    right: cloud.left + cloud.width,
    light: NEAR_LIGHT,
    random,
  });
}

/**
 * Where the rain starts above a spot: the base of the lowest cloud hanging
 * over it, in px from the top of the screen.
 */
export type CloudBase = (xFraction: number) => number;

/**
 * The storm clouds: they roll in from above when mounted, drift while it
 * rains, and roll away when `isLeaving`.
 *
 * @param props.isLeaving - True while the rain is stopping.
 * @param props.skyRef - Receives the clouds' wrapper, so lightning can light
 *     them up.
 * @param props.cloudBaseRef - Receives a {@link CloudBase} for the painted
 *     sky, so the rain can start each drop inside the cloud above it.
 * @param props.rollInMs - How long the clouds take to roll in.
 * @param props.leaveMs - How long they take to roll away.
 * @return The clouds.
 * @throws Never.
 */
export function RainClouds({
  isLeaving,
  skyRef,
  cloudBaseRef,
  rollInMs,
  leaveMs,
}: {
  isLeaving: boolean;
  skyRef: RefObject<HTMLDivElement>;
  cloudBaseRef: MutableRefObject<CloudBase | null>;
  rollInMs: number;
  leaveMs: number;
}) {
  const deckRef = useRef<HTMLCanvasElement>(null);
  const cloudCanvasesRef = useRef<(HTMLCanvasElement | null)[]>([]);
  const seedRef = useRef(Math.floor(Math.random() * 2 ** 31));
  const [plan, setPlan] = useState<SkyPlan>(() =>
    planSky({
      screenWidth: window.innerWidth,
      random: seededRandom(seedRef.current),
    }),
  );

  useEffect(() => {
    let pending = 0;
    const onResize = () => {
      window.clearTimeout(pending);
      pending = window.setTimeout(() => {
        setPlan(
          planSky({
            screenWidth: window.innerWidth,
            random: seededRandom(seedRef.current),
          }),
        );
      }, 150);
    };
    window.addEventListener('resize', onResize);
    return () => {
      window.clearTimeout(pending);
      window.removeEventListener('resize', onResize);
    };
  }, []);

  useEffect(() => {
    const ratio = Math.min(window.devicePixelRatio || 1, 1.5);
    const random = seededRandom(seedRef.current + 1);
    const deck = deckRef.current;
    const deckContext = deck?.getContext('2d');
    if (deck && deckContext) {
      deck.width = Math.round(plan.deckWidth * ratio);
      deck.height = Math.round(plan.deckHeight * ratio);
      deck.style.width = `${plan.deckWidth}px`;
      deck.style.height = `${plan.deckHeight}px`;
      deckContext.setTransform(ratio, 0, 0, ratio, 0, 0);
      deckContext.clearRect(0, 0, plan.deckWidth, plan.deckHeight);
      paintDeck({
        context: deckContext,
        width: plan.deckWidth,
        scale: plan.scale,
        random,
      });
    }
    plan.clouds.forEach((cloud, index) => {
      const canvas = cloudCanvasesRef.current[index];
      if (canvas) {
        paintCumulus({canvas, cloud, ratio, random});
      }
    });
    cloudBaseRef.current = xFraction => {
      const x = xFraction * window.innerWidth;
      let base = BARE_SKY_BASE * plan.scale;
      for (const cloud of plan.clouds) {
        if (x >= cloud.spanLeft && x <= cloud.spanRight) {
          base = Math.max(base, cloud.baseY);
        }
      }
      return base;
    };
    return () => {
      cloudBaseRef.current = null;
    };
  }, [plan, cloudBaseRef]);

  return (
    <Sky ref={skyRef} aria-hidden>
      <Roll
        $isLeaving={isLeaving}
        $delayMs={0}
        $rollInMs={rollInMs}
        $leaveMs={leaveMs}
      >
        <DeckCanvas ref={deckRef} />
      </Roll>
      {plan.clouds.map((cloud, index) => {
        const motion: CSSProperties & Record<`--${string}`, string> = {
          left: cloud.left,
          top: cloud.top,
          width: cloud.width,
          height: cloud.height,
          '--drift-px': `${cloud.driftPx.toFixed(1)}px`,
          '--drift-s': `${cloud.driftSec.toFixed(1)}s`,
          '--bob-s': `${cloud.bobSec.toFixed(1)}s`,
          '--phase-s': `-${cloud.phaseSec.toFixed(1)}s`,
        };
        return (
          <Roll
            key={`${cloud.left.toFixed(0)}-${cloud.baseY.toFixed(0)}`}
            $isLeaving={isLeaving}
            $delayMs={200 + cloud.arriveMs}
            $rollInMs={rollInMs}
            $leaveMs={leaveMs}
          >
            <CloudCanvas
              ref={element => {
                cloudCanvasesRef.current[index] = element;
              }}
              style={motion}
            />
          </Roll>
        );
      })}
    </Sky>
  );
}

const fadeIn = keyframes`
  from { opacity: 0; }
  to { opacity: 1; }
`;

const fadeOut = keyframes`
  from { opacity: 1; }
  to { opacity: 0; }
`;

const rollIn = keyframes`
  from { transform: translateY(-${SKY_HEIGHT + 60}px); }
  to { transform: translateY(0); }
`;

const rollOut = keyframes`
  from { transform: translateY(0); }
  to { transform: translateY(-${SKY_HEIGHT + 60}px); }
`;

const deckDrift = keyframes`
  from { transform: translateX(-${DRIFT_PX - 10}px); }
  to { transform: translateX(${DRIFT_PX - 10}px); }
`;

/** Each cloud's own drift, sized by its custom properties. */
const cloudDrift = keyframes`
  from { translate: calc(-1 * var(--drift-px)) 0; }
  to { translate: var(--drift-px) 0; }
`;

/**
 * A slow rise and fall of a few px, on top of the drift. It moves the
 * transform, which composes with the drift's translate, so neither forces a
 * layout.
 */
const cloudBob = keyframes`
  from { transform: translateY(0); }
  to { transform: translateY(6px); }
`;

const Sky = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
`;

/** Rolls a layer of clouds in from above, or away again. */
const Roll = styled.div<{
  $isLeaving: boolean;
  $delayMs: number;
  $rollInMs: number;
  $leaveMs: number;
}>`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  animation: ${({$isLeaving, $delayMs, $rollInMs, $leaveMs}) =>
    $isLeaving
      ? css`
          ${rollOut} ${$leaveMs}ms ease-in ${$delayMs / 3}ms forwards
        `
      : css`
          ${rollIn} ${$rollInMs}ms cubic-bezier(0.3, 0.5, 0.35, 1) ${$delayMs}ms both
        `};

  @media (prefers-reduced-motion: reduce) {
    animation: ${({$isLeaving, $rollInMs, $leaveMs}) =>
      $isLeaving
        ? css`
            ${fadeOut} ${$leaveMs}ms forwards
          `
        : css`
            ${fadeIn} ${$rollInMs}ms both
          `};
  }
`;

/** The far deck drifts slowly as one. */
const DeckCanvas = styled.canvas`
  position: absolute;
  top: 0;
  left: -${DRIFT_PX}px;
  display: block;
  animation: ${deckDrift} 40s ease-in-out infinite alternate-reverse;

  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }
`;

/** One near cloud, drifting and bobbing at its own pace. */
const CloudCanvas = styled.canvas`
  position: absolute;
  display: block;
  animation:
    ${cloudDrift} var(--drift-s) ease-in-out var(--phase-s) infinite alternate,
    ${cloudBob} var(--bob-s) ease-in-out var(--phase-s) infinite alternate;

  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }
`;
